/**
 * StudioPatternLibrary.tsx — Biblioteca de blocos prontos (dois níveis)
 *
 * Primeiro nível: as categorias (Capa, Recursos, Preços, Rodapé...).
 * Segundo nível: os modelos prontos daquela categoria, cada um com uma
 * miniatura. Um clique insere o modelo já preenchido no ponto escolhido do
 * documento.
 *
 * A lista vem SEMPRE do catálogo de padrões do Studio Core — esta tela não
 * mantém lista própria. Quem precisa de um bloco cru continua tendo a aba
 * "Blocos", que reaproveita a biblioteca já existente.
 *
 * RELEASE: v1.3.0 - 2026-09-10 - Coluna de categorias mais estreita, cartões
 *          sem bordas e com sombras maiores.
 * RELEASE: v1.2.0 - 2026-09-10 - Painel alto, rolagens independentes e
 *          galeria ampla com prévias reais.
 * RELEASE: v1.1.0 - 2026-09-10 - Adaptada ao painel contextual esquerdo.
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, construtor de blocos prontos)
 */

import React, { useMemo, useState } from 'react';
import * as Icones from 'lucide-react';
import { ChevronLeft, Search, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

import {
  listPatternCategories,
  listPatterns,
  type StudioOutput,
  type StudioTemplateType,
} from '@/lib/studio/core';
import { StudioPatternThumb } from './StudioPatternThumb';
import { StudioBlockLibrary } from './StudioBlockLibrary';

interface StudioPatternLibraryProps {
  /** Formas de entrega do modelo — filtram os padrões compatíveis. */
  outputs: StudioOutput[];
  /** Tipo do modelo em edição. */
  templateType?: StudioTemplateType;
  /** Posição do documento onde a inserção acontece. */
  insertIndex: number | null;
  /** Insere um bloco pronto. */
  onInsertPattern: (patternKey: string, indice: number | null) => void;
  /** Insere um bloco cru do catálogo. */
  onInsertBlock: (blockKey: string, indice: number | null) => void;
  /** Fecha o painel. */
  onClose: () => void;
}

/** Resolve o ícone da categoria pelo nome usado no catálogo. */
function IconeDaCategoria({ nome, className }: { nome: string; className?: string }) {
  const chave = nome
    .split('-')
    .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
    .join('');
  const Componente = (Icones as unknown as Record<string, React.ComponentType<{ className?: string }>>)[chave];
  const Final = Componente ?? Icones.Square;
  return <Final className={className} />;
}

export function StudioPatternLibrary({
  outputs,
  templateType,
  insertIndex,
  onInsertPattern,
  onInsertBlock,
  onClose,
}: StudioPatternLibraryProps) {
  const [categoria, setCategoria] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [modoBlocos, setModoBlocos] = useState(false);

  const filtros = useMemo(() => ({ outputs, templateType }), [outputs, templateType]);

  const categorias = useMemo(() => listPatternCategories(filtros), [filtros]);

  const padroes = useMemo(() => {
    const termo = busca.trim();
    if (termo) return listPatterns({ ...filtros, search: termo });
    if (!categoria) return [];
    return listPatterns({ ...filtros, category: categoria });
  }, [busca, categoria, filtros]);

  const categoriaAtual = categorias.find((c) => c.key === categoria) ?? null;
  const mostrandoGaleria = Boolean(categoria) || busca.trim().length > 0;

  return (
    <div className="flex h-full min-h-0 min-w-0 bg-card font-body text-card-foreground">
      {/* ------------------------------------------------- Nível 1: categorias */}
      <div className="flex h-full min-h-0 w-44 shrink-0 flex-col bg-muted/20 sm:w-48">
        <div className="flex h-14 shrink-0 items-center justify-between gap-2 px-4">
          <div>
            <p className="font-display text-sm font-semibold">Adicionar bloco</p>
            <p className="text-[10px] text-muted-foreground">Escolha uma categoria</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onClose}
            aria-label="Fechar biblioteca"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="shrink-0 p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar"
              className="h-9 bg-background pl-8 text-xs shadow-sm"
            />
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          <div className="space-y-1 p-3">
            {categorias.map((item) => (
              <Button
                key={item.key}
                type="button"
                variant="ghost"
                onClick={() => {
                  setBusca('');
                  setModoBlocos(false);
                  setCategoria(item.key);
                }}
                className={cn(
                  'h-9 w-full justify-start gap-2 rounded-full px-3 text-left text-xs font-medium transition-colors',
                  categoria === item.key && !modoBlocos
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <IconeDaCategoria nome={item.icon} className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 truncate">{item.label}</span>
                <span className="text-[10px] text-muted-foreground">{item.count}</span>
              </Button>
            ))}

            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setBusca('');
                setCategoria(null);
                setModoBlocos(true);
              }}
              className={cn(
                'mt-2 h-9 w-full justify-start gap-2 rounded-full px-3 text-left text-xs font-medium transition-colors',
                modoBlocos
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
            >
              <Icones.Boxes className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 truncate">Blocos avulsos</span>
            </Button>
          </div>
        </ScrollArea>
      </div>

      {/* ---------------------------------------------- Nível 2: modelos prontos */}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-card">
        {modoBlocos ? (
          <>
            <div className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setModoBlocos(false)}
                aria-label="Voltar para as categorias"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <p className="text-xs font-semibold">Blocos avulsos</p>
            </div>
            <div className="min-h-0 flex-1">
              <StudioBlockLibrary
                outputs={outputs}
                templateType={templateType}
                onAdd={(chave) => onInsertBlock(chave, insertIndex)}
              />
            </div>
          </>
        ) : !mostrandoGaleria ? (
          <div className="flex h-full items-center justify-center p-8 text-center">
            <div className="max-w-xs rounded-xl border border-dashed bg-muted/20 px-6 py-8">
              <Icones.LayoutGrid className="mx-auto mb-3 h-6 w-6 text-muted-foreground/60" />
              <p className="text-xs font-medium text-foreground">Escolha uma categoria</p>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                Os modelos prontos aparecerão aqui com o resultado visual completo.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex min-h-14 shrink-0 flex-col justify-center px-4 py-2">
              <p className="font-display text-sm font-semibold">
                {busca.trim() ? 'Resultados da busca' : categoriaAtual?.label}
              </p>
              {!busca.trim() && categoriaAtual?.description && (
                <p className="text-[11px] text-muted-foreground">{categoriaAtual.description}</p>
              )}
            </div>

            <ScrollArea className="min-h-0 flex-1">
              <div className="grid grid-cols-1 gap-5 p-5 xl:grid-cols-2">
                {padroes.map((padrao) => (
                  <Button
                    key={padrao.key}
                    type="button"
                    variant="ghost"
                    onClick={() => onInsertPattern(padrao.key, insertIndex)}
                    className="group h-auto w-full min-w-0 flex-col items-stretch rounded-xl bg-card p-3 text-left font-normal shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-studio-accent motion-reduce:transform-none"
                  >
                    <StudioPatternThumb nodes={padrao.build()} />
                    <p className="mt-2.5 text-xs font-semibold">{padrao.label}</p>
                    {padrao.description && (
                      <p className="text-[11px] text-muted-foreground">{padrao.description}</p>
                    )}
                  </Button>
                ))}

                {padroes.length === 0 && (
                  <p className="col-span-full px-1 py-6 text-center text-xs text-muted-foreground">
                    Nenhum modelo encontrado.
                  </p>
                )}
              </div>
            </ScrollArea>
          </>
        )}
      </div>
    </div>
  );
}
